import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, ExternalLink, Save, X } from "lucide-react";
import { Link } from "react-router-dom";

const CATEGORIES = ["decouverte", "alimentation", "ig-index", "complications", "vie-quotidienne"];

const empty = {
  title: "",
  subtitle: "",
  category: "alimentation",
  excerpt: "",
  content: "",
  keywords: "",
  reading_time_min: 5,
  published: true,
};

export default function AdminGuides() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/guides");
      setItems(r.data.items || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    const payload = {
      ...editing,
      keywords: typeof editing.keywords === "string"
        ? editing.keywords.split(",").map((k) => k.trim()).filter(Boolean)
        : editing.keywords,
      reading_time_min: Number(editing.reading_time_min) || 5,
    };
    try {
      if (editing.id) {
        await api.patch(`/admin/guides/${editing.id}`, payload);
        toast.success("Guide mis à jour");
      } else {
        await api.post("/admin/guides", payload);
        toast.success("Guide créé");
      }
      setEditing(null);
      load();
    } catch (e) {
      toast.error("Erreur de sauvegarde");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer ce guide ?")) return;
    try {
      await api.delete(`/admin/guides/${id}`);
      toast.success("Guide supprimé");
      load();
    } catch (e) { toast.error("Erreur"); }
  };

  return (
    <div data-testid="admin-guides-page">
      <div className="flex items-baseline justify-between mb-6">
        <div>
          <p className="overline mb-2">{items.length} guides</p>
          <h1 className="font-serif text-4xl">Guides</h1>
        </div>
        <button onClick={() => setEditing({ ...empty })} data-testid="admin-new-guide-btn" className="btn-primary">
          <Plus className="w-4 h-4" strokeWidth={1.5} /> Nouveau guide
        </button>
      </div>

      {editing && (
        <div className="bg-white border border-[hsl(var(--border-default))] p-6 rounded-sm mb-8" data-testid="admin-guide-editor">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-serif text-2xl">{editing.id ? "Éditer guide" : "Nouveau guide"}</h2>
            <button onClick={() => setEditing(null)} data-testid="admin-guide-close-btn" className="text-[hsl(var(--text-muted))]"><X className="w-5 h-5" strokeWidth={1.5} /></button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <input placeholder="Titre" value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} data-testid="guide-title-input"
              className="px-3 py-2 text-sm bg-white border border-[hsl(var(--border-light))] rounded-sm md:col-span-2" />
            <input placeholder="Sous-titre" value={editing.subtitle || ""} onChange={(e) => setEditing({ ...editing, subtitle: e.target.value })}
              className="px-3 py-2 text-sm bg-white border border-[hsl(var(--border-light))] rounded-sm md:col-span-2" />
            <select value={editing.category} onChange={(e) => setEditing({ ...editing, category: e.target.value })}
              className="px-3 py-2 text-sm bg-white border border-[hsl(var(--border-light))] rounded-sm">
              {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <input type="number" placeholder="Temps de lecture (min)" value={editing.reading_time_min} onChange={(e) => setEditing({ ...editing, reading_time_min: e.target.value })}
              className="px-3 py-2 text-sm bg-white border border-[hsl(var(--border-light))] rounded-sm" />
            <textarea placeholder="Excerpt (résumé court)" value={editing.excerpt} onChange={(e) => setEditing({ ...editing, excerpt: e.target.value })}
              className="px-3 py-2 text-sm bg-white border border-[hsl(var(--border-light))] rounded-sm md:col-span-2" rows={2} />
            <textarea placeholder="Contenu Markdown (## Titre, **gras**, listes...)" value={editing.content} onChange={(e) => setEditing({ ...editing, content: e.target.value })} data-testid="guide-content-textarea"
              className="px-3 py-2 text-sm font-mono bg-white border border-[hsl(var(--border-light))] rounded-sm md:col-span-2" rows={18} />
            <input placeholder="Mots-clés (séparés par virgule)" value={typeof editing.keywords === "string" ? editing.keywords : (editing.keywords || []).join(", ")}
              onChange={(e) => setEditing({ ...editing, keywords: e.target.value })}
              className="px-3 py-2 text-sm bg-white border border-[hsl(var(--border-light))] rounded-sm md:col-span-2" />
          </div>
          <div className="mt-4 flex gap-3">
            <button onClick={save} data-testid="guide-save-btn" className="btn-primary"><Save className="w-4 h-4" strokeWidth={1.5} /> Enregistrer</button>
            <button onClick={() => setEditing(null)} className="btn-outline">Annuler</button>
          </div>
        </div>
      )}

      <div className="bg-white border border-[hsl(var(--border-light))] rounded-sm overflow-hidden">
        <table className="w-full text-sm" data-testid="admin-guides-table">
          <thead className="bg-[hsl(var(--bg-subtle))]">
            <tr>
              <th className="text-left px-4 py-3 font-medium">Titre</th>
              <th className="text-left px-4 py-3 font-medium hidden md:table-cell">Catégorie</th>
              <th className="text-left px-4 py-3 font-medium hidden md:table-cell">Vues</th>
              <th className="text-right px-4 py-3 font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={4} className="px-4 py-8 text-center text-[hsl(var(--text-muted))]">Chargement…</td></tr>
            ) : items.map((g) => (
              <tr key={g.id} className="border-t border-[hsl(var(--border-light))] hover:bg-[hsl(var(--bg-subtle))]/50">
                <td className="px-4 py-3">
                  <div className="font-medium">{g.title}</div>
                  <div className="text-xs text-[hsl(var(--text-muted))]">{g.slug}</div>
                </td>
                <td className="px-4 py-3 hidden md:table-cell">{g.category}</td>
                <td className="px-4 py-3 hidden md:table-cell">{g.views || 0}</td>
                <td className="px-4 py-3 text-right space-x-3 whitespace-nowrap">
                  <button onClick={() => setEditing({ ...g, keywords: (g.keywords || []).join(", ") })} className="text-[hsl(var(--brand-primary))] hover:underline text-xs uppercase tracking-wider">Éditer</button>
                  <Link to={`/guides/${g.slug}`} target="_blank" className="inline-flex items-center text-[hsl(var(--text-secondary))] hover:text-[hsl(var(--brand-primary))]">
                    <ExternalLink className="w-4 h-4" strokeWidth={1.5} />
                  </Link>
                  <button onClick={() => remove(g.id)} className="text-[hsl(var(--brand-primary))]">
                    <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
